import { MINUTE } from '@enterprise/core';

import type { PermissionsProviderProps } from './types/permissions-provider.props';

import { usePermissionsProviderConfiguration } from './hooks/permissions-provider-configuration.hook';
import { PermissionsContext } from './permissions.context';

/**
 * Provides the permissions context to its child components.
 *
 * This component manages authorization state and cache clearing functionality,
 * using the configuration provided via props. It wraps its children with the
 * `PermissionsContext.Provider`, making permission-related utilities available
 * throughout the component tree.
 *
 * @param children - The React nodes to render within the provider.
 * @param isAuthorized - Optional external authorization function or value.
 * @param sliding - If true, enables sliding expiration for the authorization cache.
 * @param ttlInMilliseconds - Time-to-live for the authorization cache, in milliseconds.
 */
export const PermissionsProvider = ({
  children,
  isAuthorized: externalIsAuthorized,
  sliding = false,
  ttlInMilliseconds = 1 * MINUTE,
}: PermissionsProviderProps) => {
  const { clearCache, isAuthorized } = usePermissionsProviderConfiguration({
    isAuthorized: externalIsAuthorized,
    sliding,
    ttlInMilliseconds,
  });

  return (
    <PermissionsContext.Provider
      value={{
        clearCache,
        isAuthorized,
      }}>
      {children}
    </PermissionsContext.Provider>
  );
};
